import PhotoSwipe from 'photoswipe';
import { prepareLightboxGallery } from './product-gallery-data.mjs';
import { lockPageScroll } from './scroll-lock';

const iconMarkup = (src) => {
    const image = document.createElement('img');
    Object.assign(image, { src, width: 24, height: 24, alt: '', className: 'pswp__icn' });
    return image.outerHTML;
};

// Match the header search icon; PhotoSwipe toggles the vertical bar when zoomed in.
const zoomIconMarkup = `<svg class="pswp__icn" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21 16.66 16.66M8 11h6"/>
        <path class="pswp__zoom-icn-bar-v" d="M11 8v6"/>
    </g>
</svg>`;

const readDetailImage = (link) => ({
    src: link.href,
    width: Number(link.dataset.fullWidth),
    height: Number(link.dataset.fullHeight),
    msrc: link.querySelector('img')?.src,
    alt: link.querySelector('img')?.alt || '',
    caption: link.dataset.caption || '',
});

/** The viewer owns gestures and focus; the hero remains the source of gallery state. */
export const createProductLightbox = (page, gallery, config) => {
    const labels = config.gallery;
    const productName = page.querySelector('h1')?.textContent.trim() || '';
    let active = null;

    page.addEventListener('click', (event) => {
        const trigger = event.target.closest('a[data-product-enlarge]');
        if (!trigger || event.defaultPrevented || event.button !== 0
            || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const detail = trigger.closest('[data-product-gallery]') ? null : readDetailImage(trigger);
        const source = prepareLightboxGallery(gallery.getItems(), gallery.getIndex(), detail);
        if (!source) return;
        event.preventDefault();
        event.stopPropagation();
        if (active) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let footer;
        let unlock;
        const viewer = new PhotoSwipe({
            dataSource: source.items,
            index: source.index,
            mainClass: 'ProductLightbox',
            bgOpacity: 1,
            showHideAnimationType: 'fade',
            showAnimationDuration: reducedMotion ? 0 : 220,
            hideAnimationDuration: reducedMotion ? 0 : 180,
            zoomAnimationDuration: reducedMotion ? 0 : 240,
            imageClickAction: 'zoom',
            tapAction: false,
            doubleTapAction: 'zoom',
            clickToCloseNonZoomable: false,
            pinchToClose: false,
            allowPanToNext: false,
            wheelToZoom: true,
            returnFocus: false,
            preload: [1, 1],
            preloaderDelay: 250,
            secondaryZoomLevel: (zoom) => Math.max(1, zoom.initial * 2),
            maxZoomLevel: (zoom) => Math.max(2, zoom.initial * 4),
            paddingFn: () => {
                const gutter = parseFloat(getComputedStyle(viewer.element).getPropertyValue('--gallery-gutter')) || 16;
                return { top: viewer.topBar.offsetHeight + 16, bottom: footer.offsetHeight + 16, left: gutter, right: gutter };
            },
            closeTitle: config.close,
            zoomTitle: labels.zoomIn,
            arrowPrevTitle: labels.previous,
            arrowNextTitle: labels.next,
            errorMsg: labels.error,
            indexIndicatorSep: ' / ',
            closeSVG: iconMarkup(`${config.assets}header/close.svg`),
            zoomSVG: zoomIconMarkup,
            arrowPrevSVG: iconMarkup(`${config.assets}slider/arrow-left.svg`),
            arrowNextSVG: iconMarkup(`${config.assets}slider/arrow-right.svg`),
        });
        active = viewer;

        viewer.addFilter('uiElement', (element, data) => {
            if (data.isButton) element.classList.add('Button--no-style');
            if (data.name === 'counter') element.setAttribute('aria-hidden', 'true');
            if (data.name === 'preloader') {
                element.setAttribute('role', 'img');
                element.setAttribute('aria-label', labels.loading);
                const update = () => element.setAttribute('aria-hidden', String(!viewer.currSlide?.content.isLoading()));
                viewer.on('change', update);
                viewer.on('loadComplete', update);
            }
            if (data.name === 'zoom') {
                const update = () => {
                    const slide = viewer.currSlide;
                    const zoomed = slide && slide.currZoomLevel > slide.zoomLevels.initial + 0.01;
                    element.title = zoomed ? labels.zoomOut : labels.zoomIn;
                    element.setAttribute('aria-label', element.title);
                    element.setAttribute('aria-pressed', String(Boolean(zoomed)));
                };
                viewer.on('zoomPanUpdate', update);
                viewer.on('change', update);
            }
            return element;
        });
        // Let the thumbnail rail scroll normally, independently of photo gestures.
        viewer.addFilter('preventPointerEvent', (prevent, pointerEvent) => (
            pointerEvent.target.closest('.ProductLightbox__footer') ? false : prevent
        ));
        viewer.addFilter('contentErrorElement', (element, content) => {
            const message = document.createElement('p');
            message.textContent = labels.error;
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'Button--no-style ProductLightbox__retry';
            retry.textContent = labels.retry;
            retry.addEventListener('click', () => viewer.refreshSlideContent(content.index));
            element.replaceChildren(message, retry);
            return element;
        });

        viewer.on('uiRegister', () => {
            viewer.ui.registerElement({
                name: 'productTitle', className: 'ProductLightbox__title', tagName: 'p', order: 1,
                onInit: (element) => { element.textContent = productName; },
            });
            viewer.ui.registerElement({
                name: 'photoStatus', className: 'ProductLightbox__status', order: 2,
                onInit: (element) => {
                    element.setAttribute('role', 'status');
                    element.setAttribute('aria-atomic', 'true');
                    viewer.on('change', () => {
                        element.textContent = config.photo.replace('{current}', viewer.currIndex + 1).replace('{total}', source.items.length);
                    });
                },
            });
            viewer.ui.registerElement({
                name: 'galleryFooter', className: 'ProductLightbox__footer', appendTo: 'root',
                onInit: (element) => {
                    footer = element;
                    const caption = document.createElement('p');
                    caption.className = 'ProductLightbox__caption';
                    caption.hidden = !source.items.some((item) => item.caption);
                    const rail = document.createElement('div');
                    rail.className = 'ProductLightbox__thumbnails';
                    rail.setAttribute('role', 'group');
                    rail.setAttribute('aria-label', labels.thumbnails);
                    rail.hidden = source.items.length < 2;
                    const buttons = source.items.map((item, index) => {
                        const button = document.createElement('button');
                        button.type = 'button';
                        button.className = 'Button--no-style ProductLightbox__thumbnail';
                        button.setAttribute('aria-label', config.photo.replace('{current}', index + 1).replace('{total}', source.items.length));
                        button.setAttribute('aria-controls', 'pswp__items');
                        const image = document.createElement('img');
                        Object.assign(image, { src: item.msrc || item.src, alt: '', width: 64, height: 64, decoding: 'async', draggable: false });
                        button.append(image);
                        button.addEventListener('click', () => viewer.goTo(index));
                        rail.append(button);
                        return button;
                    });
                    rail.addEventListener('keydown', (keyEvent) => {
                        if (keyEvent.altKey || keyEvent.ctrlKey || keyEvent.metaKey || keyEvent.shiftKey) return;
                        const destinations = { ArrowLeft: viewer.currIndex - 1, ArrowRight: viewer.currIndex + 1, Home: 0, End: buttons.length - 1 };
                        if (!(keyEvent.key in destinations)) return;
                        keyEvent.preventDefault();
                        keyEvent.stopPropagation();
                        const index = (destinations[keyEvent.key] + buttons.length) % buttons.length;
                        viewer.goTo(index);
                        buttons[index].focus({ preventScroll: true });
                    });
                    footer.addEventListener('wheel', (wheelEvent) => wheelEvent.stopPropagation(), { passive: true });
                    footer.append(caption, rail);
                    footer.hidden = caption.hidden && rail.hidden;
                    viewer.on('change', () => {
                        caption.textContent = source.items[viewer.currIndex].caption || '';
                        buttons.forEach((button, index) => {
                            button.tabIndex = index === viewer.currIndex ? 0 : -1;
                            if (index === viewer.currIndex) button.setAttribute('aria-current', 'true');
                            else button.removeAttribute('aria-current');
                        });
                        const selected = buttons[viewer.currIndex].getBoundingClientRect();
                        const bounds = rail.getBoundingClientRect();
                        const overflow = selected.left < bounds.left ? selected.left - bounds.left
                            : Math.max(0, selected.right - bounds.right);
                        if (overflow) rail.scrollTo({ left: rail.scrollLeft + overflow, behavior: reducedMotion ? 'instant' : 'smooth' });
                    });
                },
            });
        });
        viewer.on('beforeOpen', () => { unlock = lockPageScroll(); });
        viewer.on('firstUpdate', () => {
            viewer.element.setAttribute('aria-modal', 'true');
            viewer.element.setAttribute('aria-label', labels.title.replace('{name}', productName));
        });
        viewer.on('bindEvents', () => viewer.element.querySelector('.pswp__button--close')?.focus({ preventScroll: true }));
        viewer.on('change', () => {
            const item = source.items[viewer.currIndex];
            if (item.galleryIndex !== null) gallery.select(item.galleryIndex);
        });
        viewer.on('destroy', () => {
            unlock?.();
            active = null;
            // Wait until PhotoSwipe has removed its own focus trap and DOM.
            queueMicrotask(() => {
                const target = trigger.closest('[hidden], [inert]') ? page.querySelector('[data-gallery-zoom]') : trigger;
                if (target?.isConnected) target.focus({ preventScroll: true });
            });
        });
        viewer.init();
    });
};
